import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  message,
  Progress,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DIMENSION_NAMES,
  DIMENSION_ORDER,
  Diagnosis,
  getDiagnosis,
  getQuestions,
  Question,
  saveAnswers,
  submitDiagnosis,
} from '../../api/diagnosis';

type AnswersMap = Record<string, number>;

/** 行业 value → 中文（与首页下拉一致） */
const INDUSTRY_CN: Record<string, string> = {
  domestic_ecom: '国内电商',
  cross_border_ecom: '跨境电商',
  logistics: '物流货代',
  trade_manufacturing: '外贸制造',
  other: '其他',
};

/** 5 个模块主题色（方案 2：侧栏 + 卡片色彩化） */
const MODULE_COLORS: Record<string, string> = {
  strategy: '#722ed1',
  process: '#1677ff',
  technology: '#13c2c2',
  data: '#52c41a',
  talent: '#fa8c16',
};

export default function DiagnosisPage() {
  const navigate = useNavigate();
  const { id: diagnosisId } = useParams<{ id: string }>();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState<AnswersMap>({});
  const [submitting, setSubmitting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [company, setCompany] = useState<Diagnosis | null>(null);
  const [cardOpen, setCardOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [, setTick] = useState(0);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const answersRef = useRef<AnswersMap>({});

  // 每秒刷新"已保存 X 秒前"
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    if (!diagnosisId) {
      navigate('/');
      return;
    }
    getQuestions()
      .then((qs) => {
        const sorted = [...qs].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
        setQuestions(sorted);
      })
      .catch(() => message.error('加载题库失败'))
      .finally(() => setLoading(false));
    getDiagnosis(diagnosisId)
      .then((d) => setCompany(d))
      .catch(() => undefined);
  }, [diagnosisId, navigate]);

  const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);

  const flushSave = useCallback(
    (id: string, map: AnswersMap): Promise<unknown> => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      if (Object.keys(map).length === 0) return Promise.resolve();
      return saveAnswers(
        id,
        Object.entries(map).map(([questionCode, score]) => ({ questionCode, score })),
      )
        .then(() => { setLastSavedAt(Date.now()); return; })
        .catch(() => message.warning('自动保存失败，请检查网络'));
    },
    [],
  );

  const persistAnswers = useCallback(
    (id: string, map: AnswersMap) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveAnswers(
          id,
          Object.entries(map).map(([questionCode, score]) => ({ questionCode, score })),
        )
          .then(() => setLastSavedAt(Date.now()))
          .catch(() => message.warning('自动保存失败，请检查网络'));
      }, 800);
    },
    [],
  );

  const handleAnswer = (code: string, score: number) => {
    const isNewAnswer = answers[code] === undefined;
    const next = { ...answers, [code]: score };
    setAnswers(next);
    if (diagnosisId) persistAnswers(diagnosisId, next);
    // 首次作答后自动进入下一题（修改答案不跳转）
    if (isNewAnswer) {
      setCurrentIndex((i) => (i < questions.length - 1 ? i + 1 : i));
    }
  };

  const handleSubmit = async () => {
    if (!diagnosisId) return;
    if (answeredCount < questions.length) {
      message.warning(`还有 ${questions.length - answeredCount} 题未作答`);
      return;
    }
    setSubmitting(true);
    try {
      // Flush any pending auto-save before submit
      await flushSave(diagnosisId, answersRef.current);
      await submitDiagnosis(diagnosisId);
      message.success('诊断提交成功！正在生成报告...');
      navigate(`/report/${diagnosisId}`);
    } catch (e) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;

  const isMobile = false;
  const progress = Math.round((answeredCount / questions.length) * 100);

  const currentQuestion = questions[currentIndex] ?? null;
  const savedAgo = lastSavedAt !== null ? Math.max(0, Math.floor((Date.now() - lastSavedAt) / 1000)) : null;
  const dimQsOfCurrent = currentQuestion ? questions.filter((q) => q.dimension === currentQuestion.dimension) : [];
  const dimIdx = currentQuestion ? dimQsOfCurrent.findIndex((q) => q.code === currentQuestion.code) : 0;
  const dimCount = dimQsOfCurrent.length;

  return (
    <div
      style={{
        maxWidth: 1080,
        margin: '0 auto',
        padding: isMobile ? '10px 10px 24px' : '16px 16px 32px',
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start',
      }}
    >
      {/* ============ 左侧侧栏（桌面） ============ */}
      {!isMobile && (
        <div style={{ flex: '0 0 240px', position: 'sticky', top: 84 }}>
          <Card size="small" style={{ borderRadius: 12 }}>
            <div style={{ textAlign: 'center', padding: '6px 0 10px', borderBottom: '1px dashed #f0f0f0', marginBottom: 10 }}>
              <div
                style={{
                  width: 76,
                  height: 76,
                  margin: '0 auto',
                  borderRadius: '50%',
                  background: `conic-gradient(#1677ff 0% ${progress}%, #f0f0f0 ${progress}% 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#1677ff', lineHeight: 1.2 }}>
                    {progress}%
                  </span>
                  <span style={{ fontSize: 10, color: '#8c8c8c' }}>{answeredCount}/25</span>
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 6 }}>答题进度</div>
            </div>
            {DIMENSION_ORDER.map((dim) => {
              const dimQs = questions.filter((q) => q.dimension === dim);
              const color = MODULE_COLORS[dim] ?? '#1677ff';
              return (
                <div key={dim} style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color, fontWeight: 600, marginBottom: 4 }}>
                    {DIMENSION_NAMES[dim]}
                  </div>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {dimQs.map((q, idx) => {
                      const answered = answers[q.code] !== undefined;
                      const active = q.code === currentQuestion?.code;
                      const qIndex = questions.findIndex((qq) => qq.code === q.code);
                      return (
                        <a
                          key={q.code}
                          href={`#q-${q.code}`}
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentIndex(qIndex);
                          }}
                          style={{
                            display: 'inline-block',
                            width: 32,
                            height: 26,
                            lineHeight: '24px',
                            textAlign: 'center',
                            borderRadius: 5,
                            fontSize: 12,
                            background: active ? color : answered ? color : '#f0f0f0',
                            color: active || answered ? '#fff' : '#999',
                            textDecoration: 'none',
                            border: '1px solid ' + (active ? color : answered ? color : '#e0e0e0'),
                            boxShadow: active ? `0 0 0 2px ${color}40` : undefined,
                          }}
                        >
                          {idx + 1}
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <Button
              type="primary"
              block
              icon={<CheckOutlined />}
              loading={submitting}
              onClick={handleSubmit}
              disabled={answeredCount < questions.length}
              style={{ marginTop: 4 }}
            >
              {answeredCount < questions.length ? `还差 ${questions.length - answeredCount} 题` : '提交诊断'}
            </Button>
          </Card>
        </div>
      )}

      {/* ============ 右侧主区 ============ */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Card style={{ marginBottom: 12, borderRadius: 12 }}>
          <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap', rowGap: 4 }}>
            <Typography.Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>
              企业数字化健康度诊断
            </Typography.Title>
            <Space size={8}>
              {!isMobile && lastSavedAt !== null && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  ✓ 已自动保存 {savedAgo} 秒前
                </Typography.Text>
              )}
              <Tag color="blue">
                已完成 {answeredCount}/{questions.length} 题
              </Tag>
              {answeredCount === questions.length && currentIndex < questions.length - 1 && (
                <Button
                  type="primary"
                  size={isMobile ? 'small' : 'middle'}
                  icon={<CheckOutlined />}
                  onClick={handleSubmit}
                  loading={submitting}
                >
                  交卷
                </Button>
              )}
            </Space>
          </Space>
          {!isMobile && (
            <Progress percent={progress} status={progress === 100 ? 'success' : 'active'} style={{ marginTop: 8 }} />
          )}
          {company && (company.companyName || company.industry || company.scale || company.revenue) && (
            <div
              style={{
                marginTop: 8,
                background: '#f0f5ff',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                color: '#0958d9',
                lineHeight: 1.7,
              }}
            >
              {isMobile
                ? `📋 ${company.companyName ?? '企业'}`
                : `📋 诊断企业：${company.companyName ?? '—'} ｜ 行业：${company.industry ? (INDUSTRY_CN[company.industry] ?? company.industry) : '—'} ｜ 规模：${company.scale ?? '—'} ｜ 营收：${company.revenue ?? '—'}`}
            </div>
          )}
          {/* 手机端答题卡折叠 */}
          {isMobile && (
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <Typography.Text strong style={{ fontSize: 13 }}>答题卡</Typography.Text>
              <Space size={6}>
                <Tag color="blue">已完成 {answeredCount}/{questions.length}</Tag>
                <Button size="small" onClick={() => setCardOpen((o) => !o)}>
                  {cardOpen ? '收起 ▴' : '展开 ▾'}
                </Button>
              </Space>
            </div>
          )}
          {isMobile && (cardOpen || true) && (
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 5 }}>
              {questions.map((q, idx) => {
                const answered = answers[q.code] !== undefined;
                const active = q.code === currentQuestion?.code;
                const color = MODULE_COLORS[q.dimension] ?? '#1677ff';
                return (
                  <a
                    key={q.code}
                    href={`#q-${q.code}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentIndex(idx);
                    }}
                    style={{
                      display: 'inline-block',
                      width: 26,
                      height: 22,
                      lineHeight: '20px',
                      textAlign: 'center',
                      borderRadius: 4,
                      fontSize: 11,
                      background: active ? color : answered ? color : '#f0f0f0',
                      color: active || answered ? '#fff' : '#666',
                      textDecoration: 'none',
                      border: '1px solid ' + (active ? color : answered ? color : '#d9d9d9'),
                    }}
                  >
                    {idx + 1}
                  </a>
                );
              })}
            </div>
          )}
        </Card>

        {currentQuestion && (
          <Card
            style={{
              marginBottom: 12,
              borderRadius: 12,
              borderLeft: `4px solid ${MODULE_COLORS[currentQuestion.dimension] ?? '#1677ff'}`,
            }}
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <Tag color="purple" style={{ background: `${MODULE_COLORS[currentQuestion.dimension] ?? '#722ed1'}`, border: 'none', color: '#fff' }}>
                {DIMENSION_NAMES[currentQuestion.dimension]} · 第 {dimIdx + 1}/{dimCount} 题
              </Tag>
              <Tag>第 {currentIndex + 1} / {questions.length} 题</Tag>
              {currentQuestion.hasNa && (
                <Tag color="orange">无此业务可选"不适用"</Tag>
              )}
            </div>
            <Typography.Paragraph strong style={{ fontSize: isMobile ? 15 : 16, marginTop: 0, lineHeight: 1.7 }}>
              {currentQuestion.questionText}
            </Typography.Paragraph>
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 8 }}>
              {currentQuestion.options.map((o) => {
                const selected = answers[currentQuestion.code] === o.score;
                const color = MODULE_COLORS[currentQuestion.dimension] ?? '#1677ff';
                return (
                  <div
                    key={o.score}
                    onClick={() => handleAnswer(currentQuestion.code, o.score)}
                    style={{
                      border: selected ? `2px solid ${color}` : '1px solid #e0e0e0',
                      background: selected ? `${color}14` : '#fff',
                      borderRadius: 10,
                      padding: isMobile ? '13px 16px' : '12px 16px',
                      cursor: 'pointer',
                      fontSize: isMobile ? 14.5 : 14,
                      lineHeight: 1.7,
                      color: '#1f2329',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      transition: 'all .15s',
                    }}
                  >
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        border: selected ? `6px solid ${color}` : '1px solid #d9d9d9',
                        background: '#fff',
                        flexShrink: 0,
                        display: 'inline-block',
                      }}
                    />
                    {o.text}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        <Card
          style={
            isMobile
              ? { position: 'sticky', bottom: 0, borderRadius: 10, boxShadow: '0 -4px 16px rgba(0,0,0,0.08)', border: 'none' }
              : { borderRadius: 12 }
          }
        >
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Button
              size={isMobile ? 'middle' : 'large'}
              style={isMobile ? { height: 42, minWidth: 96 } : undefined}
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              disabled={currentIndex === 0}
            >
              ← 上一题
            </Button>
            {currentIndex === questions.length - 1 ? (
              <Button
                type="primary"
                size={isMobile ? 'middle' : 'large'}
                style={isMobile ? { height: 42, minWidth: 96 } : undefined}
                icon={<CheckOutlined />}
                loading={submitting}
                onClick={handleSubmit}
                disabled={answeredCount < questions.length}
              >
                {answeredCount < questions.length ? `还差 ${questions.length - answeredCount} 题` : '提交诊断'}
              </Button>
            ) : (
              <Button
                type="primary"
                size={isMobile ? 'middle' : 'large'}
                style={isMobile ? { height: 42, minWidth: 96 } : undefined}
                onClick={() => setCurrentIndex((i) => Math.min(questions.length - 1, i + 1))}
              >
                下一题 →
              </Button>
            )}
          </Space>
        </Card>
      </div>
    </div>
  );
}